import { Button, Chip } from '@mui/material';
import {
  SecurityOutlined as SecurityIcon,
  Shield,
  Edit as EditIcon,
  GetApp,
  Delete as DeleteIcon,
  Assignment as AssignIcon,
  AssignmentReturn as UnassignIcon,
  CategoryOutlined as CategoryIcon,
} from '@mui/icons-material';
import Timeline from '@/components/Timeline/Timeline';
import { useGetRoleHistory } from '@/api/hooks/roles/useGetRoleHistory';

const RoleHistorySection = ({ roleId }) => {
  const { history, loading, error } = useGetRoleHistory(roleId);

  const getTimelineIconAndColor = (historyItem) => {
    switch (historyItem.action) {
      case 'CREATE':
        return { component: <SecurityIcon />, color: 'success' };
      case 'UPDATE':
      case 'UPDATE_PERMISSIONS':
        return { component: <Shield />, color: 'warning' };
      case 'UPDATE_CUSTOM_PAYMENT_TYPE_ACCESS':
        return { component: <CategoryIcon />, color: 'warning' };
      case 'ASSIGN':
        return { component: <AssignIcon />, color: 'info' };
      case 'UNASSIGN':
        return { component: <UnassignIcon />, color: 'secondary' };
      case 'DELETE':
        return { component: <DeleteIcon />, color: 'error' };
      default:
        return { component: <EditIcon />, color: 'grey' };
    }
  };

  const getActionTitle = (historyItem) => {
    switch (historyItem.action) {
      case 'CREATE':
        return 'Role Created';
      case 'UPDATE_PERMISSIONS':
        return 'Permissions Updated';
      case 'UPDATE':
        return 'Role Updated';
      case 'UPDATE_CUSTOM_PAYMENT_TYPE_ACCESS':
        return 'Custom Payment Type Access Updated';
      case 'ASSIGN':
        return 'User Assigned';
      case 'UNASSIGN':
        return 'User Removed';
      case 'DELETE':
        return 'Role Deleted';
      default:
        return historyItem.action
          .replace('_', ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const getActionDescription = (historyItem) => {
    const { action, previous_value, new_value, metadata } = historyItem;

    switch (action) {
      case 'CREATE':
        return 'Role created with initial permissions';
      case 'UPDATE_PERMISSIONS':
        return metadata?.reason || 'Role permissions were updated';
      case 'UPDATE':
        if (previous_value?.name !== new_value?.name) {
          return `Role name changed from "${previous_value?.name}" to "${new_value?.name}"`;
        }
        if (previous_value?.description !== new_value?.description) {
          return 'Role description was updated';
        }
        return 'Role information was updated';
      case 'UPDATE_CUSTOM_PAYMENT_TYPE_ACCESS':
        if (
          previous_value?.custom_payment_type_access_enabled !==
          new_value?.custom_payment_type_access_enabled
        ) {
          return new_value?.custom_payment_type_access_enabled
            ? 'Custom payment type access was enabled'
            : 'Custom payment type access was disabled';
        }
        return 'Allowed custom payment types were updated';
      case 'ASSIGN':
        return `Role assigned to user ${new_value?.user_email || 'user'}`;
      case 'UNASSIGN':
        return `Role removed from user ${previous_value?.user_email || 'user'}`;
      case 'DELETE':
        return 'Role was deactivated';
      default:
        return `Role ${action.toLowerCase()}`;
    }
  };

  const timelineConfig = {
    getId: (item) => item.id,
    getDate: (item) => item.changed_at,
    getIcon: getTimelineIconAndColor,
    getTitle: getActionTitle,
    getDescription: getActionDescription,
    getMetadata: (item) => ({
      primary: `Changed by: ${
        item.changed_by?.name ||
        item.changed_by?.email ||
        `User ${item.changed_by}`
      }`,
    }),
    renderActions: (item) =>
      item.metadata?.downloadable_file && (
        <Button
          size='small'
          startIcon={<GetApp />}
          variant='outlined'
          onClick={() => {
            console.log('Downloading:', item.metadata.downloadable_file);
            alert(`Downloading: ${item.metadata.downloadable_file}`);
          }}>
          Download
        </Button>
      ),
    renderExtra: (item, context) =>
      (item.previous_value || item.new_value || item.metadata) && (
        <Chip
          size='small'
          label='View details'
          variant='outlined'
          color='default'
          onClick={() => context?.openModal?.(item)}
        />
      ),
    dateFormat: 'short',
    empty: {
      message: !roleId
        ? 'Role ID is required to view history'
        : 'No history entries found for this role',
    },
    loading: {
      skeletonCount: 3,
      skeletonHeight: 80,
    },
    detailsModal: {
      enabled: true,
      config: {
        getTitle: getActionTitle,
        getIcon: getTimelineIconAndColor,
        showValueDiff: true,
        valueDiffMode: 'inline',
      },
    },
  };

  return (
    <Timeline
      items={history}
      config={timelineConfig}
      loading={loading}
      error={error}
      title='Role History'
      subtitle='Timeline of all changes made to this role'
    />
  );
};

export default RoleHistorySection;
